import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Flag, Pause, Play, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { act, useFetch } from "@/lib/hooks";
import { Loading, PageHeader, Panel, Photos, StatusBadge } from "@/components/common";
import { selectCls } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function PartsPanel({ o, locked, reload }: { o: any; locked: boolean; reload: () => void }) {
  const [parts, setParts] = useState<any[]>([]);
  const [pick, setPick] = useState("");
  const [qty, setQty] = useState(1);
  useEffect(() => {
    api.get("/mechanic/parts").then((r) => setParts(r.data));
  }, [o.parts_used.length]);
  const add = async () => {
    if (pick && (await act(() => api.post(`/mechanic/orders/${o.id}/parts`, { item_id: pick, qty }), "Pièce sortie du stock"))) {
      setPick("");
      setQty(1);
      reload();
    }
  };
  const remove = async (eid: string) => {
    if (await act(() => api.delete(`/mechanic/orders/${o.id}/parts/${eid}`), "Pièce remise en stock")) reload();
  };
  return (
    <Panel title="Pièces">
      {o.parts_planned.length > 0 && <div className="mb-4 text-sm text-muted-foreground">Prévu : {o.parts_planned.map((p: any) => `${p.qty} × ${p.label}`).join(" · ")}</div>}
      {o.parts_used.map((p: any) => (
        <div key={p.id} className="flex items-center justify-between border-b border-border py-3 text-base">
          <span>{p.qty} × {p.label}</span>
          {!locked && <button onClick={() => remove(p.id)} className="p-2 text-muted-foreground hover:text-red-400" data-testid={`mech-part-remove-${p.id}`}><Trash2 className="h-5 w-5" /></button>}
        </div>
      ))}
      {!locked && (
        <div className="mt-4 grid grid-cols-[1fr_90px_auto] gap-2">
          <select className={`${selectCls} h-14 text-base`} value={pick} onChange={(e) => setPick(e.target.value)} data-testid="mech-part-select">
            <option value="">Choisir une pièce…</option>
            {parts.map((p) => <option key={p.id} value={p.id} disabled={p.stock_qty <= 0}>{p.label} (stock {p.stock_qty})</option>)}
          </select>
          <Input type="number" min={1} value={qty} onChange={(e) => setQty(Number(e.target.value))} className="h-14 text-lg" data-testid="mech-part-qty" />
          <Button className="h-14 px-6 text-base" onClick={add} data-testid="mech-part-add-btn">Sortir</Button>
        </div>
      )}
    </Panel>
  );
}

export default function MechanicOrder() {
  const { id } = useParams();
  const { data: o, reload } = useFetch<any>(`/mechanic/orders/${id}`);
  const [note, setNote] = useState("");
  if (!o) return <Loading />;
  const locked = ["termine", "qc_ok", "cloture"].includes(o.status);
  const post = async (path: string, body: any, msg?: string) => {
    if (await act(() => api.post(`/mechanic/orders/${o.id}/${path}`, body), msg)) reload();
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="mechanic-order-view">
      <PageHeader eyebrow={`${o.number} · ${o.vehicle_label}`} title={o.plate}>
        <div className="mt-3"><StatusBadge status={o.status} testId="mech-order-status" /></div>
      </PageHeader>
      <div className="space-y-6">
        {!locked && (
          <button onClick={() => post("timer", { action: o.running ? "stop" : "start" }, o.running ? "Chrono arrêté" : "Chrono lancé")}
            className={`flex h-24 w-full items-center justify-center gap-4 rounded-sm font-display text-2xl font-bold uppercase tracking-wider text-white transition-colors ${o.running ? "bg-amber-600 hover:bg-amber-700" : "bg-primary hover:bg-[#D62828]"}`} data-testid="mech-timer-btn">
            {o.running ? <><Pause className="h-8 w-8" />Pause</> : <><Play className="h-8 w-8" />Démarrer le travail</>}
          </button>
        )}
        <div className="text-center text-sm text-muted-foreground" data-testid="mech-total-time">Temps total : {Math.floor(o.total_minutes / 60)} h {o.total_minutes % 60} min</div>
        {o.instructions && <Panel title="Consignes"><p className="text-lg">{o.instructions}</p></Panel>}
        <Panel title="Tâches">
          <div className="space-y-3">
            {o.tasks.map((t: any, i: number) => (
              <button key={i} disabled={locked} onClick={() => post("task", { index: i, done: !t.done })} data-testid={`mech-task-${i}`}
                className={`flex min-h-16 w-full items-center gap-4 rounded-sm border p-4 text-left text-lg transition-colors ${t.done ? "border-emerald-500/60 bg-emerald-500/10" : "border-border hover:bg-accent"}`}>
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border-2 ${t.done ? "border-emerald-400 bg-emerald-500 text-white" : "border-border"}`}>{t.done && <Check className="h-6 w-6" />}</span>
                {t.label}
              </button>
            ))}
          </div>
        </Panel>
        <PartsPanel o={o} locked={locked} reload={reload} />
        <Panel title="Photos"><Photos entity="orders" entityId={o.id} photos={o.photos} onAdded={reload} canUpload={!locked} big /></Panel>
        <Panel title="Notes">
          {o.notes.map((n: any, i: number) => <div key={i} className="border-l-2 border-border py-2 pl-3"><div>{n.text}</div><div className="text-xs text-muted-foreground">{n.by}</div></div>)}
          {!locked && (
            <div className="mt-3 flex gap-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Observation, pièce à prévoir…" className="h-14 text-base" data-testid="mech-note-input" />
              <Button className="h-14" onClick={() => { if (note) { post("note", { text: note }); setNote(""); } }} data-testid="mech-note-add-btn">Ajouter</Button>
            </div>
          )}
        </Panel>
        {!locked && (
          <Button className="h-20 w-full bg-emerald-600 text-xl font-bold uppercase tracking-wider hover:bg-emerald-700" onClick={() => post("finish", {}, "Travail terminé, en attente du contrôle qualité")} data-testid="mech-finish-btn">
            <Flag className="mr-3 h-6 w-6" />Travail terminé
          </Button>
        )}
        {o.qc && o.qc.result === "refuse" && <Panel title="Retour contrôle qualité"><p className="text-red-300">{o.qc.comment}</p></Panel>}
      </div>
    </div>
  );
}
